import Navbar from '../components/Navbar';
import { Mail, Clock, MapPin, Package, Users, FileText, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

function Contact() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Navbar />
      
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" data-testid="contact-page">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 animate-fade-in">
            <div className="inline-block mb-4">
              <span className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold">
                Get in Touch
              </span>
            </div>
            <h1 className="font-bold text-4xl md:text-6xl tracking-tight leading-tight mb-6" data-testid="contact-title">
              Contact BlueCrate
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We're here to help you source high-quality building materials at unbeatable pricing. 
              Whether you're a developer ready to place a pledge or a supplier exploring partnerships, 
              our team is here to support you.
            </p>
          </div>

          {/* Primary Contact Card */}
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-gray-100 mb-12">
            <div className="flex items-center justify-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Mail className="w-8 h-8 text-primary" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-center mb-4">Email</h2>
            <a 
              href="mailto:hello@bluecrategroup.com" 
              className="text-2xl md:text-3xl font-bold text-primary hover:text-primary/80 transition-colors block text-center mb-4"
              data-testid="primary-email"
            >
              hello@bluecrategroup.com
            </a>
            <p className="text-center text-gray-600">We respond within 24 hours.</p>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* For Developers & Builders */}
            <div className="bg-white rounded-3xl shadow-lg p-8 border border-gray-100">
              <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-6">
                <Package className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-2xl font-bold mb-4">For Developers & Builders</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-primary mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Request Pricing</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-primary mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Join the Waitlist</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-primary mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Ask About Sample Kits</span>
                </li>
              </ul>
              <Button 
                className="w-full mt-6 rounded-full bg-primary hover:bg-primary/90"
                onClick={() => window.location.href = 'mailto:hello@bluecrategroup.com?subject=Developer Inquiry'}
                data-testid="developer-contact-button"
              >
                Contact Us
              </Button>
            </div>

            {/* Supplier Partnerships */}
            <div className="bg-white rounded-3xl shadow-lg p-8 border border-gray-100">
              <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-accent" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Supplier Partnerships</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                If you're interested in supplying building materials, please email:
              </p>
              <a 
                href="mailto:hello@bluecrategroup.com" 
                className="text-lg font-bold text-primary hover:text-primary/80 transition-colors block mb-4"
                data-testid="supplier-email"
              >
                hello@bluecrategroup.com
              </a>
              <div className="bg-gray-50 rounded-2xl p-4 mt-4">
                <p className="text-sm text-gray-600 mb-2 font-semibold">Please include:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Your catalog</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>MOQ (Minimum Order Quantity)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Pricing tiers</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Lead times</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Company Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Headquarters */}
            <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-3xl p-8 border border-primary/10">
              <div className="flex items-center mb-4">
                <MapPin className="w-6 h-6 text-primary mr-3" />
                <h3 className="text-xl font-bold">Headquarters</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                <strong>BlueCrate Group</strong><br />
                Sheridan, Wyoming (incorporated)<br />
                Operating Nationwide
              </p>
            </div>

            {/* Response Hours */}
            <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-3xl p-8 border border-primary/10">
              <div className="flex items-center mb-4">
                <Clock className="w-6 h-6 text-primary mr-3" />
                <h3 className="text-xl font-bold">Response Hours</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                <strong>Monday–Friday</strong><br />
                9AM–6PM CST
              </p>
            </div>
          </div>

          {/* Trust Statement */}
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border-2 border-primary/20 text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-2xl font-bold mb-4">Trust & Transparency</h3>
            <p className="text-lg text-gray-700 leading-relaxed max-w-2xl mx-auto">
              We take pride in transparent pricing, reliable logistics, and hands-on support.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center mb-4">
            <img src="/bluecrate-logo.jpg" alt="BlueCrate" className="h-10" />
          </div>
          <p className="text-sm text-gray-600">
            © 2025 BlueCrate Group. Premium building materials through cooperative shipping.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default Contact;
